import { useState } from 'react';
import {
  SHIPPING_ZONE_ORDER,
  SHIPPING_ZONE_LABELS,
  DEFAULT_RATE_MATRIX,
  DEFAULT_SHIPPING_CONFIG,
  type ShippingRateMatrix,
  type ShippingZoneKey,
} from '@/services/shipping';

// Lista de ciudades editables por sector. `national_municipal` no tiene lista
// porque es el sector por defecto (cubre cualquier ciudad no clasificada).
export interface ShippingZoneCityLists {
  local: string;
  regional: string;
  national_metro: string;
  national_municipal: string;
  difficult: string;
  veredas: string;
}

export const EMPTY_ZONE_CITIES: ShippingZoneCityLists = {
  local: '',
  regional: '',
  national_metro: '',
  national_municipal: '',
  difficult: '',
  veredas: '',
};

// Listas de ciudades base de Interrapidísimo (para el botón "Restaurar").
const DEFAULT_ZONE_CITIES: ShippingZoneCityLists = {
  local: DEFAULT_SHIPPING_CONFIG.localCities.join(', '),
  regional: DEFAULT_SHIPPING_CONFIG.regionalCities.join(', '),
  national_metro: DEFAULT_SHIPPING_CONFIG.nationalMetroCities.join(', '),
  national_municipal: '',
  difficult: DEFAULT_SHIPPING_CONFIG.difficultCities.join(', '),
  veredas: DEFAULT_SHIPPING_CONFIG.veredasCities.join(', '),
};

// Copia profunda de la matriz base para que "Restaurar" no comparta arrays.
function cloneDefaultMatrix(): ShippingRateMatrix {
  return SHIPPING_ZONE_ORDER.reduce((acc, key) => {
    acc[key] = {
      base: DEFAULT_RATE_MATRIX[key].base,
      additional: [...DEFAULT_RATE_MATRIX[key].additional],
    };
    return acc;
  }, {} as ShippingRateMatrix);
}

interface Props {
  matrix: ShippingRateMatrix;
  cities: ShippingZoneCityLists;
  onMatrixChange: (matrix: ShippingRateMatrix) => void;
  onCitiesChange: (cities: ShippingZoneCityLists) => void;
}

function toInt(value: string): number {
  const n = parseInt(value, 10);
  return Number.isFinite(n) ? n : 0;
}

const inputClass =
  'w-full text-sm border border-background-300 rounded-lg px-3 py-2 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300';

export default function RateMatrixEditor({
  matrix,
  cities,
  onMatrixChange,
  onCitiesChange,
}: Props) {
  const [confirmingReset, setConfirmingReset] = useState(false);

  function setBase(zone: ShippingZoneKey, value: string) {
    onMatrixChange({ ...matrix, [zone]: { ...matrix[zone], base: toInt(value) } });
  }

  function setTier(zone: ShippingZoneKey, index: number, value: string) {
    const tiers = [...matrix[zone].additional];
    tiers[index] = toInt(value);
    onMatrixChange({ ...matrix, [zone]: { ...matrix[zone], additional: tiers } });
  }

  function addTier(zone: ShippingZoneKey) {
    const tiers = [...matrix[zone].additional];
    tiers.push(tiers.length > 0 ? tiers[tiers.length - 1] : 0);
    onMatrixChange({ ...matrix, [zone]: { ...matrix[zone], additional: tiers } });
  }

  function removeTier(zone: ShippingZoneKey, index: number) {
    const tiers = matrix[zone].additional.filter((_, i) => i !== index);
    onMatrixChange({ ...matrix, [zone]: { ...matrix[zone], additional: tiers } });
  }

  function setCity(zone: ShippingZoneKey, value: string) {
    onCitiesChange({ ...cities, [zone]: value });
  }

  function restoreDefaults() {
    onMatrixChange(cloneDefaultMatrix());
    onCitiesChange({ ...DEFAULT_ZONE_CITIES });
    setConfirmingReset(false);
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-start gap-3">
        <div className="flex items-start gap-2 rounded-lg bg-primary-50 border border-primary-100 px-3.5 py-3 flex-1">
          <i className="ri-information-line text-primary-600 mt-0.5"></i>
          <p className="text-xs text-foreground-600 leading-relaxed">
            Cada sector combina una <strong>tarifa de kilo inicial</strong> con un{' '}
            <strong>escalafón de kilo adicional</strong> por distancia. El sistema suma los pesos de
            las tallas (peso × cantidad) y aplica un escalón por cada kilo por encima de 1 kg. El{' '}
            <strong>primer escalón es el valor por defecto</strong>.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setConfirmingReset(true)}
          disabled={confirmingReset}
          className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-secondary-300 text-secondary-700 bg-white hover:bg-secondary-50 transition-colors text-xs font-medium whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
        >
          <i className="ri-refresh-line"></i>
          Restaurar valores base de Interrapidísimo
        </button>
      </div>

      {confirmingReset && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3">
          <div className="flex items-start gap-2 flex-1">
            <i className="ri-error-warning-line text-amber-600 mt-0.5"></i>
            <p className="text-xs text-amber-800 leading-relaxed">
              Se restaurarán los <strong>6 sectores</strong> y sus <strong>listas de ciudades</strong>{' '}
              a los valores base de Interrapidísimo. Se perderán los ajustes actuales (aún no guardados).
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={() => setConfirmingReset(false)}
              className="px-3.5 py-2 rounded-lg border border-background-300 text-foreground-600 bg-white hover:bg-background-100 transition-colors text-xs font-medium whitespace-nowrap cursor-pointer"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={restoreDefaults}
              className="px-3.5 py-2 rounded-lg bg-secondary-500 text-white hover:bg-secondary-600 transition-colors text-xs font-medium whitespace-nowrap cursor-pointer"
            >
              Sí, restaurar
            </button>
          </div>
        </div>
      )}

      {SHIPPING_ZONE_ORDER.map((zone) => {
        const rate = matrix[zone];
        const isDefaultZone = zone === 'national_municipal';
        return (
          <div
            key={zone}
            className="rounded-lg border border-background-200 bg-background-50 p-4 space-y-3"
          >
            <div className="flex items-center gap-2">
              <span className="w-7 h-7 rounded-md bg-primary-100 text-primary-700 flex items-center justify-center flex-shrink-0">
                <i className="ri-road-map-line text-sm"></i>
              </span>
              <h3 className="text-sm font-semibold text-foreground-900">
                {SHIPPING_ZONE_LABELS[zone]}
              </h3>
              {isDefaultZone && (
                <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 font-medium whitespace-nowrap">
                  Por defecto
                </span>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-foreground-600 mb-1.5 block">
                  Kilo inicial ($)
                </label>
                <input
                  type="number"
                  min="0"
                  step="100"
                  value={rate.base}
                  onChange={(e) => setBase(zone, e.target.value)}
                  className={inputClass}
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-foreground-600 mb-1.5 block">
                Escalafón kilo adicional ($)
              </label>
              <div className="flex flex-wrap items-center gap-2">
                {rate.additional.map((tier, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-1 rounded-lg border border-background-300 bg-white pl-2 pr-1 py-1"
                  >
                    <span className="text-[10px] text-foreground-400 font-medium whitespace-nowrap">
                      #{index + 1}
                    </span>
                    <input
                      type="number"
                      min="0"
                      step="100"
                      value={tier}
                      onChange={(e) => setTier(zone, index, e.target.value)}
                      className="w-24 text-sm bg-transparent text-foreground-900 focus:outline-none"
                    />
                    <button
                      type="button"
                      onClick={() => removeTier(zone, index)}
                      className="w-6 h-6 flex items-center justify-center rounded-md text-foreground-400 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                      aria-label="Quitar escalón"
                    >
                      <i className="ri-close-line text-sm"></i>
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => addTier(zone)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-dashed border-primary-300 text-primary-700 bg-white hover:bg-primary-50 transition-colors text-xs font-medium whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-add-line"></i>Agregar escalón
                </button>
              </div>
            </div>

            {isDefaultZone ? (
              <p className="text-xs text-foreground-400">
                Este es el sector aplicado automáticamente a las ciudades que no estén en otra lista.
              </p>
            ) : (
              <div>
                <label className="text-xs font-medium text-foreground-600 mb-1.5 block">
                  Ciudades del sector (separadas por coma)
                </label>
                <textarea
                  rows={2}
                  value={cities[zone]}
                  onChange={(e) => setCity(zone, e.target.value)}
                  placeholder="Ej: Bogotá, Soacha"
                  className={`${inputClass} resize-none`}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}